import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast
} from "@chakra-ui/react";
import { api, resolveAssetUrl } from "../../api";

const emptyItem = (order = 100) => ({
  title: "",
  subtitle: "",
  description: "",
  badge: "",
  image_url: "",
  icon: "",
  price_label: "",
  price_value: "",
  price_suffix: "",
  cta_label: "",
  cta_url: "",
  highlight: false,
  is_active: true,
  display_order: order,
  list_json: []
});

const joinLines = (value) => (Array.isArray(value) ? value.join("\n") : "");

const splitLines = (value) =>
  String(value || "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);

const uploadLandingAsset = async (entity, file) => {
  if (!file) return "";
  const formData = new FormData();
  formData.append("file", file);
  const response = await api.post(`/v2/landing/upload/${entity}`, formData, {
    headers: { "Content-Type": "multipart/form-data" }
  });
  return response.data?.url || "";
};

const LandingAdminPage = ({ empresaId }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [site, setSite] = useState(null);
  const [sections, setSections] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadFilters, setLeadFilters] = useState({ status: "", q: "" });

  const load = useCallback(async (showToast = false) => {
    setLoading(true);
    try {
      const [bundleResponse, leadsResponse] = await Promise.all([
        api.get("/v2/landing"),
        api.get("/v2/landing/leads")
      ]);
      setSite(bundleResponse.data?.site || null);
      setSections(bundleResponse.data?.sections || []);
      setLeads(leadsResponse.data?.items || []);
      if (showToast) {
        toast({ title: "Landing atualizada", status: "success", duration: 2400, isClosable: true });
      }
    } catch (_error) {
      toast({ title: "Erro ao carregar landing", status: "error", duration: 3200, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredLeads = useMemo(() => {
    return leads.filter((lead) => {
      const matchesStatus = leadFilters.status ? lead.status === leadFilters.status : true;
      const query = leadFilters.q.trim().toLowerCase();
      const matchesQuery = query
        ? [lead.nome, lead.empresa, lead.email, lead.telefone].some((value) =>
            String(value || "").toLowerCase().includes(query)
          )
        : true;
      return matchesStatus && matchesQuery;
    });
  }, [leads, leadFilters]);

  const updateSection = (sectionKey, updater) => {
    setSections((current) =>
      current.map((section) =>
        section.section_key === sectionKey ? { ...section, ...updater(section) } : section
      )
    );
  };

  const updateSectionField = (sectionKey, field, value) => {
    updateSection(sectionKey, () => ({ [field]: value }));
  };

  const updateItemField = (sectionKey, itemIndex, field, value) => {
    updateSection(sectionKey, (section) => ({
      items: section.items.map((item, index) =>
        index === itemIndex
          ? {
              ...item,
              [field]: field === "list_json" ? splitLines(value) : value
            }
          : item
      )
    }));
  };

  const addItem = (sectionKey) => {
    updateSection(sectionKey, (section) => ({
      items: [...(section.items || []), emptyItem((section.items?.length || 0) * 10 + 100)]
    }));
  };

  const handleSaveSite = async () => {
    try {
      const response = await api.put("/v2/landing/site", site);
      setSite(response.data);
      toast({ title: "Configurações da landing salvas", status: "success", duration: 2200, isClosable: true });
    } catch (_error) {
      toast({ title: "Erro ao salvar configuracoes", status: "error", duration: 2800, isClosable: true });
    }
  };

  const handleSaveSection = async (section) => {
    try {
      const response = await api.put(`/v2/landing/sections/${section.section_key}`, section);
      updateSection(section.section_key, () => response.data);
      toast({ title: `Seção ${section.label || section.section_key} salva`, status: "success", duration: 2200, isClosable: true });
    } catch (_error) {
      toast({ title: "Erro ao salvar seção", status: "error", duration: 2800, isClosable: true });
    }
  };

  const handleSaveItem = async (sectionKey, item) => {
    try {
      if (item.id) {
        await api.put(`/v2/landing/sections/${sectionKey}/items/${item.id}`, item);
      } else {
        await api.post(`/v2/landing/sections/${sectionKey}/items`, item);
      }
      await load();
    } catch (_error) {
      toast({ title: "Erro ao salvar item", status: "error", duration: 2800, isClosable: true });
    }
  };

  const handleDeleteItem = async (sectionKey, item) => {
    if (!item.id) {
      updateSection(sectionKey, (section) => ({
        items: section.items.filter((current) => current !== item)
      }));
      return;
    }

    try {
      await api.delete(`/v2/landing/sections/${sectionKey}/items/${item.id}`);
      await load();
    } catch (_error) {
      toast({ title: "Erro ao remover item", status: "error", duration: 2800, isClosable: true });
    }
  };

  const handleLeadSave = async (lead) => {
    try {
      await api.put(`/v2/landing/leads/${lead.id}`, {
        status: lead.status,
        qualification: lead.qualification,
        checkout_status: lead.checkout_status,
        notes: lead.notes
      });
      toast({ title: "Lead atualizado", status: "success", duration: 2000, isClosable: true });
    } catch (_error) {
      toast({ title: "Erro ao atualizar lead", status: "error", duration: 2800, isClosable: true });
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get("/v2/landing/leads/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "landing-leads.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (_error) {
      toast({ title: "Erro ao exportar leads", status: "error", duration: 2600, isClosable: true });
    }
  };

  if (loading && !site) {
    return <Text>Carregando configuracao da landing...</Text>;
  }

  return (
    <Stack spacing={6}>
      <Box className="empresa-panel-card" p={5}>
        <HStack justify="space-between" align="start" flexWrap="wrap" spacing={4}>
          <Box>
            <Text fontSize="xl" fontWeight="700">Landing comercial</Text>
            <Text color="gray.500">
              Edite SEO, textos, imagens, FAQ, planos, onboarding, captação de leads e integrações sem mexer em código. Empresa atual: #{empresaId}.
            </Text>
          </Box>
          <HStack spacing={3}>
            <Button className="empresa-action-btn" onClick={() => window.open("/", "_blank", "noopener,noreferrer")}>
              Abrir landing
            </Button>
            <Button className="empresa-primary-btn" onClick={() => load(true)}>
              Recarregar
            </Button>
          </HStack>
        </HStack>
      </Box>

      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>
        <Box className="empresa-panel-card" p={5}>
          <Text fontWeight="700" mb={4}>Configurações gerais e SEO</Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl>
              <FormLabel>Nome do site</FormLabel>
              <Input value={site?.site_name || ""} onChange={(event) => setSite((current) => ({ ...current, site_name: event.target.value }))} />
            </FormControl>
            <FormControl>
              <FormLabel>Slug interno</FormLabel>
              <Input value={site?.slug || ""} onChange={(event) => setSite((current) => ({ ...current, slug: event.target.value }))} />
            </FormControl>
            <FormControl>
              <FormLabel>Meta title</FormLabel>
              <Input value={site?.meta_title || ""} onChange={(event) => setSite((current) => ({ ...current, meta_title: event.target.value }))} />
            </FormControl>
            <FormControl>
              <FormLabel>Meta description</FormLabel>
              <Textarea rows={3} value={site?.meta_description || ""} onChange={(event) => setSite((current) => ({ ...current, meta_description: event.target.value }))} />
            </FormControl>
            <FormControl>
              <FormLabel>OG title</FormLabel>
              <Input value={site?.og_title || ""} onChange={(event) => setSite((current) => ({ ...current, og_title: event.target.value }))} />
            </FormControl>
            <FormControl>
              <FormLabel>OG description</FormLabel>
              <Textarea rows={3} value={site?.og_description || ""} onChange={(event) => setSite((current) => ({ ...current, og_description: event.target.value }))} />
            </FormControl>
            <FormControl className="landing-admin-full">
              <FormLabel>Imagem social / OG</FormLabel>
              <Input value={site?.og_image_url || ""} onChange={(event) => setSite((current) => ({ ...current, og_image_url: event.target.value }))} />
            </FormControl>
          </SimpleGrid>
        </Box>

        <Box className="empresa-panel-card" p={5}>
          <Text fontWeight="700" mb={4}>Contato, funil e integrações</Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl>
              <FormLabel>E-mail comercial</FormLabel>
              <Input value={site?.support_email || ""} onChange={(event) => setSite((current) => ({ ...current, support_email: event.target.value }))} />
            </FormControl>
            <FormControl>
              <FormLabel>Telefone comercial</FormLabel>
              <Input value={site?.support_phone || ""} onChange={(event) => setSite((current) => ({ ...current, support_phone: event.target.value }))} />
            </FormControl>
            <FormControl>
              <FormLabel>WhatsApp</FormLabel>
              <Input value={site?.whatsapp_number || ""} onChange={(event) => setSite((current) => ({ ...current, whatsapp_number: event.target.value }))} />
            </FormControl>
            <FormControl>
              <FormLabel>Checkout base URL</FormLabel>
              <Input value={site?.checkout_base_url || ""} onChange={(event) => setSite((current) => ({ ...current, checkout_base_url: event.target.value }))} />
            </FormControl>
            <FormControl>
              <FormLabel>Webhook CRM</FormLabel>
              <Input value={site?.crm_webhook_url || ""} onChange={(event) => setSite((current) => ({ ...current, crm_webhook_url: event.target.value }))} />
            </FormControl>
            <FormControl>
              <FormLabel>Token webhook</FormLabel>
              <Input value={site?.crm_webhook_token || ""} onChange={(event) => setSite((current) => ({ ...current, crm_webhook_token: event.target.value }))} />
            </FormControl>
            <FormControl>
              <FormLabel>Cor primária</FormLabel>
              <Input type="color" value={site?.primary_color || "#123447"} onChange={(event) => setSite((current) => ({ ...current, primary_color: event.target.value }))} />
            </FormControl>
            <FormControl>
              <FormLabel>Cor secundária</FormLabel>
              <Input type="color" value={site?.secondary_color || "#0f4c5c"} onChange={(event) => setSite((current) => ({ ...current, secondary_color: event.target.value }))} />
            </FormControl>
            <FormControl>
              <FormLabel>Cor destaque</FormLabel>
              <Input type="color" value={site?.accent_color || "#f59e0b"} onChange={(event) => setSite((current) => ({ ...current, accent_color: event.target.value }))} />
            </FormControl>
            <FormControl display="flex" alignItems="center" pt={8}>
              <Switch isChecked={Boolean(site?.automations_active)} onChange={(event) => setSite((current) => ({ ...current, automations_active: event.target.checked }))} />
              <Text ml={3}>Ativar automacoes de e-mail e webhook</Text>
            </FormControl>
            <FormControl className="landing-admin-full">
              <FormLabel>Mensagem de sucesso do pré-cadastro</FormLabel>
              <Textarea rows={3} value={site?.success_message || ""} onChange={(event) => setSite((current) => ({ ...current, success_message: event.target.value }))} />
            </FormControl>
            <FormControl className="landing-admin-full">
              <FormLabel>Texto do rodapé</FormLabel>
              <Textarea rows={4} value={site?.footer_text || ""} onChange={(event) => setSite((current) => ({ ...current, footer_text: event.target.value }))} />
            </FormControl>
          </SimpleGrid>
          <HStack justify="flex-end" mt={4}>
            <Button className="empresa-primary-btn" onClick={handleSaveSite}>
              Salvar configuracoes
            </Button>
          </HStack>
        </Box>
      </SimpleGrid>

      <Box className="empresa-panel-card" p={5}>
        <Text fontWeight="700" mb={4}>Seções e conteúdo administrável</Text>
        <Accordion allowMultiple>
          {sections.map((section) => (
            <AccordionItem key={section.section_key} borderTop="none" mb={3}>
              <AccordionButton border="1px solid" borderColor="gray.100" borderRadius="xl" py={4}>
                <Box flex="1" textAlign="left">
                  <Text fontWeight="700">{section.label || section.section_key}</Text>
                  <HStack spacing={3} mt={1}>
                    <Badge colorScheme={section.is_active ? "green" : "gray"}>{section.is_active ? "Ativa" : "Inativa"}</Badge>
                    <Text fontSize="sm" color="gray.500">ordem {section.display_order}</Text>
                  </HStack>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pt={5}>
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>Label</FormLabel>
                    <Input value={section.label || ""} onChange={(event) => updateSectionField(section.section_key, "label", event.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Slug / anchor</FormLabel>
                    <Input value={section.slug || ""} onChange={(event) => updateSectionField(section.section_key, "slug", event.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Eyebrow</FormLabel>
                    <Input value={section.eyebrow || ""} onChange={(event) => updateSectionField(section.section_key, "eyebrow", event.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Ordem</FormLabel>
                    <Input type="number" value={section.display_order || 0} onChange={(event) => updateSectionField(section.section_key, "display_order", Number(event.target.value))} />
                  </FormControl>
                  <FormControl className="landing-admin-full">
                    <FormLabel>Título</FormLabel>
                    <Input value={section.title || ""} onChange={(event) => updateSectionField(section.section_key, "title", event.target.value)} />
                  </FormControl>
                  <FormControl className="landing-admin-full">
                    <FormLabel>Subtítulo</FormLabel>
                    <Textarea rows={3} value={section.subtitle || ""} onChange={(event) => updateSectionField(section.section_key, "subtitle", event.target.value)} />
                  </FormControl>
                  <FormControl className="landing-admin-full">
                    <FormLabel>Descrição</FormLabel>
                    <Textarea rows={4} value={section.description || ""} onChange={(event) => updateSectionField(section.section_key, "description", event.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>CTA principal</FormLabel>
                    <Input value={section.primary_cta_label || ""} onChange={(event) => updateSectionField(section.section_key, "primary_cta_label", event.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Link CTA principal</FormLabel>
                    <Input value={section.primary_cta_url || ""} onChange={(event) => updateSectionField(section.section_key, "primary_cta_url", event.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>CTA secundário</FormLabel>
                    <Input value={section.secondary_cta_label || ""} onChange={(event) => updateSectionField(section.section_key, "secondary_cta_label", event.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Link CTA secundário</FormLabel>
                    <Input value={section.secondary_cta_url || ""} onChange={(event) => updateSectionField(section.section_key, "secondary_cta_url", event.target.value)} />
                  </FormControl>
                  <FormControl className="landing-admin-full">
                    <FormLabel>Imagem / mídia</FormLabel>
                    <Input value={section.media_url || ""} onChange={(event) => updateSectionField(section.section_key, "media_url", event.target.value)} />
                    <Input mt={2} type="file" accept="image/*" onChange={async (event) => {
                      const nextUrl = await uploadLandingAsset("section", event.target.files?.[0]);
                      if (nextUrl) updateSectionField(section.section_key, "media_url", nextUrl);
                    }} />
                    {section.media_url ? <Box mt={3}><img src={resolveAssetUrl(section.media_url)} alt={section.title || section.label} style={{ maxHeight: 160, borderRadius: 16 }} /></Box> : null}
                  </FormControl>
                  <FormControl display="flex" alignItems="center" pt={8}>
                    <Switch isChecked={Boolean(section.is_active)} onChange={(event) => updateSectionField(section.section_key, "is_active", event.target.checked)} />
                    <Text ml={3}>Seção ativa</Text>
                  </FormControl>
                </SimpleGrid>

                <HStack justify="flex-end" mt={4} mb={6}>
                  <Button className="empresa-primary-btn" onClick={() => handleSaveSection(section)}>
                    Salvar seção
                  </Button>
                </HStack>

                <HStack justify="space-between" align="center" mb={4}>
                  <Text fontWeight="700">Itens da seção</Text>
                  <Button className="empresa-action-btn" onClick={() => addItem(section.section_key)}>
                    Novo item
                  </Button>
                </HStack>

                <Stack spacing={4}>
                  {(section.items || []).map((item, index) => (
                    <Box key={item.id || `${section.section_key}-${index}`} border="1px solid" borderColor="gray.100" borderRadius="2xl" p={4}>
                      <Grid templateColumns={{ base: "1fr", lg: "repeat(2, minmax(0, 1fr))" }} gap={4}>
                        <FormControl>
                          <FormLabel>Título</FormLabel>
                          <Input value={item.title || ""} onChange={(event) => updateItemField(section.section_key, index, "title", event.target.value)} />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Subtítulo</FormLabel>
                          <Input value={item.subtitle || ""} onChange={(event) => updateItemField(section.section_key, index, "subtitle", event.target.value)} />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Badge</FormLabel>
                          <Input value={item.badge || ""} onChange={(event) => updateItemField(section.section_key, index, "badge", event.target.value)} />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Ícone</FormLabel>
                          <Input value={item.icon || ""} onChange={(event) => updateItemField(section.section_key, index, "icon", event.target.value)} />
                        </FormControl>
                        <FormControl className="landing-admin-full">
                          <FormLabel>Descrição</FormLabel>
                          <Textarea rows={3} value={item.description || ""} onChange={(event) => updateItemField(section.section_key, index, "description", event.target.value)} />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Preço label</FormLabel>
                          <Input value={item.price_label || ""} onChange={(event) => updateItemField(section.section_key, index, "price_label", event.target.value)} />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Preço valor</FormLabel>
                          <Input value={item.price_value ?? ""} onChange={(event) => updateItemField(section.section_key, index, "price_value", event.target.value)} />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Sufixo do preço</FormLabel>
                          <Input value={item.price_suffix || ""} onChange={(event) => updateItemField(section.section_key, index, "price_suffix", event.target.value)} />
                        </FormControl>
                        <FormControl>
                          <FormLabel>CTA label</FormLabel>
                          <Input value={item.cta_label || ""} onChange={(event) => updateItemField(section.section_key, index, "cta_label", event.target.value)} />
                        </FormControl>
                        <FormControl>
                          <FormLabel>CTA link</FormLabel>
                          <Input value={item.cta_url || ""} onChange={(event) => updateItemField(section.section_key, index, "cta_url", event.target.value)} />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Imagem</FormLabel>
                          <Input value={item.image_url || ""} onChange={(event) => updateItemField(section.section_key, index, "image_url", event.target.value)} />
                          <Input mt={2} type="file" accept="image/*" onChange={async (event) => {
                            const nextUrl = await uploadLandingAsset("item", event.target.files?.[0]);
                            if (nextUrl) updateItemField(section.section_key, index, "image_url", nextUrl);
                          }} />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Ordem</FormLabel>
                          <Input type="number" value={item.display_order || 0} onChange={(event) => updateItemField(section.section_key, index, "display_order", Number(event.target.value))} />
                        </FormControl>
                        <FormControl className="landing-admin-full">
                          <FormLabel>Lista de benefícios / itens (1 por linha)</FormLabel>
                          <Textarea rows={4} value={joinLines(item.list_json)} onChange={(event) => updateItemField(section.section_key, index, "list_json", event.target.value)} />
                        </FormControl>
                        <FormControl display="flex" alignItems="center">
                          <Switch isChecked={Boolean(item.is_active)} onChange={(event) => updateItemField(section.section_key, index, "is_active", event.target.checked)} />
                          <Text ml={3}>Item ativo</Text>
                        </FormControl>
                        <FormControl display="flex" alignItems="center">
                          <Switch isChecked={Boolean(item.highlight)} onChange={(event) => updateItemField(section.section_key, index, "highlight", event.target.checked)} />
                          <Text ml={3}>Destaque visual</Text>
                        </FormControl>
                      </Grid>

                      {item.image_url ? <Box mt={3}><img src={resolveAssetUrl(item.image_url)} alt={item.title || "Preview"} style={{ maxHeight: 140, borderRadius: 16 }} /></Box> : null}

                      <HStack justify="flex-end" mt={4}>
                        <Button colorScheme="red" variant="ghost" onClick={() => handleDeleteItem(section.section_key, item)}>
                          Remover
                        </Button>
                        <Button className="empresa-primary-btn" onClick={() => handleSaveItem(section.section_key, item)}>
                          Salvar item
                        </Button>
                      </HStack>
                    </Box>
                  ))}
                </Stack>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </Box>

      <Box className="empresa-panel-card" p={5}>
        <HStack justify="space-between" align="center" mb={4} flexWrap="wrap">
          <Box>
            <Text fontWeight="700">Leads capturados</Text>
            <Text color="gray.500">Acompanhe origem, interesse, qualificação e estado do checkout.</Text>
          </Box>
          <HStack spacing={3}>
            <Input placeholder="Buscar lead" value={leadFilters.q} onChange={(event) => setLeadFilters((current) => ({ ...current, q: event.target.value }))} maxW="220px" />
            <Select value={leadFilters.status} onChange={(event) => setLeadFilters((current) => ({ ...current, status: event.target.value }))} maxW="180px">
              <option value="">Todos os status</option>
              <option value="novo">Novo</option>
              <option value="contato">Contato</option>
              <option value="qualificado">Qualificado</option>
              <option value="checkout">Checkout</option>
              <option value="fechado">Fechado</option>
            </Select>
            <Button className="empresa-action-btn" onClick={handleExport}>
              Exportar CSV
            </Button>
          </HStack>
        </HStack>
        <Box overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Lead</Th>
                <Th>Contato</Th>
                <Th>Interesse</Th>
                <Th>Status</Th>
                <Th>Checkout</Th>
                <Th>Notas</Th>
                <Th>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredLeads.map((lead, index) => (
                <Tr key={lead.id}>
                  <Td>
                    <Text fontWeight="700">{lead.nome}</Text>
                    <Text color="gray.500">{lead.empresa || "Sem empresa"}</Text>
                  </Td>
                  <Td>
                    <Text>{lead.email}</Text>
                    <Text color="gray.500">{lead.telefone}</Text>
                  </Td>
                  <Td>
                    <Text>{lead.interesse_principal || "Não informado"}</Text>
                    <Text color="gray.500">{lead.plano_desejado || "Sem plano"}</Text>
                  </Td>
                  <Td>
                    <Select size="sm" value={lead.status || "novo"} onChange={(event) => {
                      const value = event.target.value;
                      setLeads((current) => current.map((item, leadIndex) => leadIndex === index ? { ...item, status: value } : item));
                    }}>
                      <option value="novo">Novo</option>
                      <option value="contato">Contato</option>
                      <option value="qualificado">Qualificado</option>
                      <option value="checkout">Checkout</option>
                      <option value="fechado">Fechado</option>
                    </Select>
                  </Td>
                  <Td>
                    <Select size="sm" value={lead.checkout_status || "pendente"} onChange={(event) => {
                      const value = event.target.value;
                      setLeads((current) => current.map((item, leadIndex) => leadIndex === index ? { ...item, checkout_status: value } : item));
                    }}>
                      <option value="pendente">Pendente</option>
                      <option value="pronto">Pronto</option>
                      <option value="enviado">Enviado</option>
                      <option value="pago">Pago</option>
                    </Select>
                  </Td>
                  <Td>
                    <Textarea size="sm" rows={2} value={lead.notes || ""} onChange={(event) => {
                      const value = event.target.value;
                      setLeads((current) => current.map((item, leadIndex) => leadIndex === index ? { ...item, notes: value } : item));
                    }} />
                  </Td>
                  <Td>
                    <Button size="sm" className="empresa-primary-btn" onClick={() => handleLeadSave(lead)}>
                      Salvar
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </Stack>
  );
};

export default LandingAdminPage;

